
import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useCommissionOffer } from "@/hooks/commission/use-commission-offers";
import { useCreateCommissionRequest } from "@/hooks/commission/use-commission-requests";
import { CommissionRequestForm } from "@/components/commissions/CommissionRequestForm";
import { useUser } from "@/hooks/use-user";
import { 
  ChevronLeft, 
  Info, 
  Clock, 
  Check, 
  Star, 
  Edit, 
  Trash2, 
  AlertCircle,
  Loader2
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function CommissionDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: isUserLoading } = useUser();
  const [activeTab, setActiveTab] = useState("details");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  
  console.log("CommissionDetails: Commission ID from params:", id);
  
  const { 
    data: offer, 
    isLoading: isOfferLoading, 
    error: offerError,
    refetch: refetchOffer
  } = useCommissionOffer(id || '');
  
  const { 
    mutate: createRequest,
    isPending: isCreatingRequest
  } = useCreateCommissionRequest();

  console.log("CommissionDetails: Offer data:", offer);
  console.log("CommissionDetails: Loading state:", isOfferLoading);
  console.log("CommissionDetails: Error state:", offerError);

  const handleRequestSubmit = (data: any) => {
    if (!user) {
      toast.error("Você precisa estar logado para solicitar uma comissão");
      return;
    }
    
    createRequest(data, {
      onSuccess: (response) => {
        toast.success("Solicitação de comissão enviada com sucesso!");
        if (response?.id) {
          navigate(`/commission-request/${response.id}`);
        } else {
          navigate("/my-commission-requests");
        }
      },
      onError: (error) => {
        console.error("Erro ao solicitar comissão:", error);
        toast.error("Erro ao solicitar comissão. Tente novamente.");
      }
    });
  };

  const handleEditCommission = () => {
    if (offer && offer.id) {
      navigate(`/commission/edit/${offer.id}`);
    }
  };

  const handleDeleteCommission = () => {
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteCommission = async () => {
    try {
      // Delete commission logic would go here
      toast.success("Comissão removida com sucesso");
      navigate("/commissions");
    } catch (error) {
      console.error("Erro ao excluir comissão:", error);
      toast.error("Erro ao excluir a comissão. Tente novamente.");
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  // Loading state
  if (isOfferLoading || isUserLoading) {
    return (
      <DashboardLayout title="Detalhes da Comissão">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p>Carregando detalhes da comissão...</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (offerError) {
    console.error("CommissionDetails: Error loading offer:", offerError);
    return (
      <DashboardLayout title="Detalhes da Comissão">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <p className="text-lg font-medium mb-2">Erro ao carregar comissão</p>
            <p className="text-muted-foreground mb-4 text-center">
              Ocorreu um erro ao carregar os detalhes da comissão. Tente novamente.
            </p>
            <div className="flex gap-2">
              <Button onClick={() => refetchOffer()} variant="outline">
                Tentar Novamente
              </Button>
              <Link to="/commissions">
                <Button>Voltar para Comissões</Button>
              </Link>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Not found state
  if (!offer) {
    return (
      <DashboardLayout title="Detalhes da Comissão">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <AlertCircle className="h-12 w-12 text-orange-500 mb-4" />
            <p className="text-lg font-medium mb-2">Comissão não encontrada</p>
            <p className="text-muted-foreground mb-4 text-center">
              A comissão que você está procurando pode não existir ou foi removida
            </p>
            <Link to="/commissions">
              <Button>Voltar para Comissões</Button>
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  const isArtistOwner = user && user.id === offer.artist_id;
  
  return (
    <DashboardLayout title="Detalhes da Comissão">
      <div className="container mx-auto pb-12 px-4 md:px-6">
        <div className="mb-6">
          <Button variant="outline" size="sm" asChild>
            <Link to="/commissions" className="flex items-center gap-1">
              <ChevronLeft className="h-4 w-4" />
              Voltar para Comissões
            </Link>
          </Button>
        </div>
        
        <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'} gap-8`}>
          <div className={isMobile ? 'order-1' : 'lg:col-span-2 order-1'}>
            <Card className="commission-card">
              <CardHeader>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <CardTitle className="text-xl sm:text-2xl break-words">{offer.title}</CardTitle>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant={offer.is_active ? "default" : "secondary"}>
                      {offer.is_active ? "Disponível" : "Indisponível"}
                    </Badge>
                    {offer.accept_nft && (
                      <Badge className="bg-amber-500">NFT</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={offer.artist?.avatar_url || ''} alt={offer.artist?.username || 'Artista'} />
                    <AvatarFallback>{offer.artist?.username?.charAt(0).toUpperCase() || 'A'}</AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1">
                    <h3 className="font-medium text-lg">{offer.artist?.username || "Artista"}</h3>
                    <div className="flex mt-0.5">
                      {Array(5).fill(0).map((_, i) => (
                        <Star 
                          key={i} 
                          className={`h-4 w-4 ${i < 4 ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}`}
                        />
                      ))}
                    </div>
                  </div>

                  {isArtistOwner && (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex items-center gap-1"
                        onClick={handleEditCommission}
                      >
                        <Edit className="h-4 w-4" />
                        {!isMobile && <span>Editar</span>}
                      </Button>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        className="flex items-center gap-1"
                        onClick={handleDeleteCommission}
                      >
                        <Trash2 className="h-4 w-4" />
                        {!isMobile && <span>Excluir</span>}
                      </Button>
                    </div>
                  )}
                </div>
                
                <Tabs 
                  value={activeTab} 
                  onValueChange={setActiveTab} 
                  className="mt-6"
                >
                  <div className="overflow-x-auto commission-tabs">
                    <TabsList className="mb-4">
                      <TabsTrigger value="details">Detalhes</TabsTrigger>
                      <TabsTrigger value="examples">Exemplos</TabsTrigger>
                      {offer.accept_nft && <TabsTrigger value="nft">NFT</TabsTrigger>}
                      {!isArtistOwner && offer.is_active && <TabsTrigger value="request">Solicitar</TabsTrigger>}
                    </TabsList>
                  </div>
                  
                  <TabsContent value="details" className="space-y-4">
                    <div>
                      <h3 className="font-semibold mb-2">Descrição</h3>
                      <p className="text-muted-foreground whitespace-pre-wrap">
                        {offer.description}
                      </p>
                    </div>
                    
                    <div className="bg-secondary/50 p-4 rounded-md space-y-3">
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-muted-foreground" />
                        <span>
                          Prazo de entrega: <span className="font-medium">{offer.delivery_time_days} dias</span>
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-muted-foreground" />
                        <span>
                          Revisões incluídas: <span className="font-medium">{offer.max_revision}</span>
                        </span>
                      </div>
                      
                      {offer.pix_key && (
                        <div className="flex items-center gap-2">
                          <span className="bg-green-500/20 p-1 rounded text-green-600 text-xs font-bold">PIX</span>
                          <span>Aceita pagamento via PIX</span>
                        </div>
                      )}
                      
                      {offer.accept_nft && (
                        <div className="flex items-center gap-2">
                          <span className="bg-amber-500/20 p-1 rounded text-amber-600 text-xs font-bold">NFT</span>
                          <span>Aceita pagamento via contrato NFT</span>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <h3 className="font-semibold mb-2">Tipos de comissão</h3>
                      <div className="flex flex-wrap gap-2">
                        {offer.specific_commissions && offer.specific_commissions.length > 0 ? (
                          offer.specific_commissions.map((type, index) => (
                            <Badge key={index} variant="outline">{type}</Badge>
                          ))
                        ) : (
                          <p className="text-muted-foreground text-sm">Sem tipos específicos definidos</p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold mb-2">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {offer.tags && offer.tags.length > 0 ? (
                          offer.tags.map((tag, index) => (
                            <Badge key={index} variant="secondary">{tag}</Badge>
                          ))
                        ) : (
                          <p className="text-muted-foreground text-sm">Sem tags definidas</p>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="examples">
                    <div>
                      <h3 className="font-semibold mb-3">Exemplos de trabalhos</h3>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {offer.examples_urls && offer.examples_urls.length > 0 ? (
                          offer.examples_urls.map((url, index) => (
                            <div key={index} className="border rounded-lg overflow-hidden">
                              <img 
                                src={url} 
                                alt={`Exemplo ${index + 1}`} 
                                className="w-full h-auto object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'https://placehold.co/300x200?text=Imagem+Inválida';
                                }}
                              />
                            </div>
                          ))
                        ) : (
                          <p className="text-muted-foreground col-span-full text-center py-12">
                            Nenhum exemplo disponível
                          </p>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                  
                  {offer.accept_nft && (
                    <TabsContent value="nft">
                      <div className="bg-gradient-to-r from-amber-500/20 to-purple-500/20 p-6 rounded-lg">
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                          <Info className="h-5 w-5" />
                          Comissão com NFT
                        </h3>
                        
                        <p className="mb-4">
                          Esta comissão utiliza um contrato inteligente NFT para garantir:
                        </p>
                        
                        <ul className="list-disc pl-5 space-y-1.5">
                          <li>Pagamento seguro em escrow liberado após aprovação</li>
                          <li>Direitos de uso exclusivos verificáveis na blockchain</li>
                          <li>Transferência automática de direitos após conclusão</li>
                          <li>Certificação de autenticidade da obra</li>
                          <li>Processo de revisão transparente e rastreável</li>
                        </ul>
                        
                        <div className="mt-4 text-sm text-muted-foreground">
                          <p>
                            Você receberá um NFT como comprovante da comissão e direitos de uso.
                            O pagamento será liberado automaticamente quando você aprovar o trabalho final.
                          </p>
                        </div>
                      </div>
                    </TabsContent>
                  )}
                  
                  {!isArtistOwner && offer.is_active && (
                    <TabsContent value="request">
                      <Card className="mt-2 border-green-500/20">
                        <CardHeader>
                          <CardTitle>Solicitar Comissão</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <CommissionRequestForm 
                            offer={offer} 
                            onSubmit={handleRequestSubmit}
                            isLoading={isCreatingRequest}
                          />
                        </CardContent>
                      </Card>
                    </TabsContent>
                  )}
                </Tabs>
              </CardContent>
            </Card>
          </div>
          
          {/* Preço - Sidebar para Desktop ou Card para Mobile */}
          <div className={isMobile ? 'order-2' : 'order-2'}>
            <div className={isMobile ? '' : 'sticky top-24'}>
              <Card>
                <CardHeader>
                  <CardTitle>Preços</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Preço base:</span>
                      <div className="space-y-1">
                        {offer.currency_type === 'USD' && (
                          <span className="font-semibold block text-right">${offer.price_usd.toFixed(2)}</span>
                        )}
                        {offer.currency_type === 'BRL' && (
                          <span className="font-semibold block text-right">R${offer.price_usd.toFixed(2)}</span>
                        )}
                        {offer.currency_type === 'EUR' && (
                          <span className="font-semibold block text-right">€{offer.price_usd.toFixed(2)}</span>
                        )}
                        {offer.accept_nft && offer.price_eth > 0 && (
                          <span className="text-amber-500 font-bold block text-right text-sm">
                            Ξ{offer.price_eth.toFixed(4)} ETH
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Prazo de entrega:</span>
                      <span className="font-semibold">{offer.delivery_time_days} dias</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Revisões incluídas:</span>
                      <span className="font-semibold">{offer.max_revision}</span>
                    </div>
                  </div>
                  
                  {!isArtistOwner && offer.is_active && (
                    <Button 
                      onClick={() => setActiveTab("request")}
                      className="w-full bg-green-500 hover:bg-green-600 text-white"
                    >
                      Solicitar Comissão
                    </Button>
                  )}
                  
                  {isArtistOwner && (
                    <div className="space-y-3">
                      <Button 
                        variant="outline" 
                        className="w-full"
                        asChild
                      >
                        <Link to="/my-store">Gerenciar Comissões</Link>
                      </Button>

                      <div className="flex gap-2">
                        <Button 
                          variant="outline"
                          className="flex-1"
                          onClick={handleEditCommission}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                        <Button 
                          variant="destructive"
                          className="flex-1"
                          onClick={handleDeleteCommission}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Excluir
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {!offer.is_active && (
                    <div className="bg-orange-100 dark:bg-orange-900/20 p-3 rounded-md text-orange-800 dark:text-orange-200 text-sm">
                      Esta comissão não está disponível no momento.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir comissão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta comissão? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteCommission}
              className="bg-destructive text-destructive-foreground"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
